var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/*
 * Copyright '2024' Dell Inc. or its subsidiaries. All Rights Reserved.
 */
import { css, html, LitElement, } from "lit";
import { property, state } from "lit/decorators.js";
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { ISiriusIconsRegistryTypeName } from "../registry/sirius-icons-registry.interface";
import { SharedDependencyContainer } from "../../../dependency-injection/shared-dependency-container";
import { SiriusIconsRegistry } from "../registry/sirius-icons.registry";
export class SiriusIconComponent extends LitElement {
    constructor() {
        var _a;
        super();
        this.alt = '';
        this._cssSize = SiriusIconComponent.T_SHIRT_SIZES[SiriusIconComponent.DEFAULT_ICON_SIZE];
        this._requestedSize = SiriusIconComponent.DEFAULT_ICON_SIZE;
        this._size = SiriusIconComponent.DEFAULT_ICON_SIZE;
        this._solid = false;
        this._failover = false;
        this._resultedResource = SiriusIconComponent.UNKNOWN_RESOURCE_SVG;
        try {
            this.iconRegistry = (_a = SharedDependencyContainer
                .getInstance()
                .getServiceCollection()
                .resolve(ISiriusIconsRegistryTypeName)) !== null && _a !== void 0 ? _a : SiriusIconsRegistry.getInstance();
        }
        catch (e) {
            this.iconRegistry = SiriusIconsRegistry.getInstance();
        }
    }
    get size() {
        return this._requestedSize;
    }
    set size(val) {
        const oldValue = this._requestedSize;
        this._requestedSize = val;
        this.requestUpdate('size', oldValue);
    }
    get solid() {
        return this._solid;
    }
    set solid(val) {
        const oldValue = this._solid;
        this._solid = val;
        this.requestUpdate('solid', oldValue);
    }
    get failover() {
        return this._failover;
    }
    set failover(val) {
        const oldValue = this._failover;
        this._failover = val;
        this.requestUpdate('failover', oldValue);
    }
    get resource() {
        return this._requestedResource;
    }
    set resource(val) {
        const oldValue = this._requestedResource;
        this._requestedResource = val;
        this.requestUpdate('asset', oldValue);
    }
    get shape() {
        return this._requestedResource;
    }
    set shape(val) {
        const oldValue = this._requestedResource;
        this._requestedResource = val;
        this.requestUpdate('asset', oldValue);
    }
    get set() {
        return this._requestedSet;
    }
    set set(val) {
        const oldValue = this._requestedSet;
        this._requestedSet = val;
        this.requestUpdate('set', oldValue);
    }
    get resultedResource() {
        return this._resultedResource || '';
    }
    set resultedResource(val) {
        const oldValue = this._resultedResource;
        this._resultedResource = val;
        this.requestUpdate('resultedResource', oldValue);
    }
    async connectedCallback() {
        super.connectedCallback();
    }
    async willUpdate(changedProperties) {
        this.computeSize();
        this.computeSet();
        await this.computeResource();
    }
    render() {
        const style = html `
            <style>
                :host {
                    font-size: 16px;
                    display: inline-block;
                    height: ${this._cssSize};
                    width: ${this._cssSize};
                    min-height: ${this._cssSize};
                    min-width: ${this._cssSize};
                    margin: 0;
                    vertical-align: middle;
                    cursor: inherit;
                }

            </style>
        `;
        if (!this.resultedResource) {
            return html ``;
        }
        return html `
            ${style}
            <div class="icon-container">
                ${this.isSvg(this.resultedResource) ?
            unsafeHTML(this.setAriaHiddenOnSvg(this.resultedResource)) :
            html `<img aria-hidden="true" alt="${this.alt}" src="${this.resultedResource}"
                                          @error="${this.onImageLoadingError}">`}
            </div>
        `;
    }
    onImageLoadingError() {
        this._icon = undefined;
        this._requestedResource = undefined;
        this._resultedResource = SiriusIconComponent.UNKNOWN_RESOURCE_SVG;
    }
    isSvg(resource) {
        return resource.trim().startsWith(SiriusIconComponent.SVG_MARKER);
    }
    setAriaHiddenOnSvg(svg) {
        if (!svg) {
            return svg;
        }
        return svg.replaceAll(SiriusIconComponent.SVG_MARKER, `${SiriusIconComponent.SVG_MARKER} aria-hidden="true" `);
    }
    getIcon(code, set, failover = true, timeout = 300000) {
        if (!code) {
            return Promise.resolve(undefined);
        }
        if (!timeout || timeout < 0) {
            timeout = 300000;
        }
        return Promise.race([
            this.iconRegistry.getIcon(code, set, failover),
            new Promise((resolve, reject) => {
                setTimeout(() => {
                    reject(new Error('Icon loading timeout'));
                }, timeout);
            })
        ]);
    }
    async computeResource() {
        const value = this.getArgument(' ', 0, this._requestedResource);
        const resource = this.getArguments(':', value).slice(-1).pop();
        try {
            const icon = await this.getIcon(resource, this.set, this.failover);
            if (!icon) {
                if (this.isNonRegistryResource(resource)) {
                    this._icon = undefined;
                    this.resultedResource = resource;
                    return;
                }
                if (resource) {
                    this.iconReadyCallback = this.onIconReady.bind(this);
                    this.iconRegistry.registerIconReadyCallback(resource, this.iconReadyCallback);
                }
            }
            this._icon = icon;
            this.resultedResource = this.getResource(this.solid, this._icon);
        }
        catch (e) {
            console.warn(e);
            this._icon = undefined;
            if (this.isNonRegistryResource(resource)) {
                this.resultedResource = resource;
                return;
            }
            this.resultedResource = this.getResource(this.solid, this._icon);
        }
    }
    getResource(solid, icon) {
        var _a;
        const variantCode = solid ? icon === null || icon === void 0 ? void 0 : icon.solidVariantCode : icon === null || icon === void 0 ? void 0 : icon.outlineVariantCode;
        return (_a = icon === null || icon === void 0 ? void 0 : icon.getVariantOrDefault(variantCode)) !== null && _a !== void 0 ? _a : SiriusIconComponent.UNKNOWN_RESOURCE_SVG;
    }
    isNonRegistryResource(resource) {
        if (!resource) {
            return true;
        }
        return resource.indexOf('://') > -1 ||
            resource.indexOf('.') > -1 ||
            resource.indexOf('/') > -1;
    }
    computeSet() {
        const value = this.getArgument(' ', 0, this._requestedResource);
        const args = this.getArguments(':', value);
        const set = args.length > 1 ? args[0] : undefined;
        this._set = set !== null && set !== void 0 ? set : this._requestedSet;
    }
    computeSize() {
        if (!this._requestedSize) {
            this._size = SiriusIconComponent.DEFAULT_ICON_SIZE;
            this._cssSize = SiriusIconComponent.T_SHIRT_SIZES[SiriusIconComponent.DEFAULT_ICON_SIZE];
        }
        if (this.isTShirtSize(this._requestedSize)) {
            this._size = this._requestedSize;
            this._cssSize = SiriusIconComponent.T_SHIRT_SIZES[this._size.toLowerCase()];
        }
        else {
            this._size = this._requestedSize;
            const cssValue = this.getCssValue(this._size);
            if (!cssValue.value) {
                this._size = SiriusIconComponent.DEFAULT_ICON_SIZE;
                this._cssSize = SiriusIconComponent.T_SHIRT_SIZES[SiriusIconComponent.DEFAULT_ICON_SIZE];
            }
            else if (!cssValue.unit) {
                if (!this.isNumeric(cssValue.value)) {
                    this._size = SiriusIconComponent.DEFAULT_ICON_SIZE;
                    this._cssSize = SiriusIconComponent.T_SHIRT_SIZES[SiriusIconComponent.DEFAULT_ICON_SIZE];
                }
                else {
                    this._cssSize = this.getPixelToRem(cssValue.value);
                }
            }
            else {
                this._cssSize = this._size;
            }
        }
    }
    isTShirtSize(val) {
        var _a;
        return Object.keys(SiriusIconComponent.T_SHIRT_SIZES).indexOf((_a = val === null || val === void 0 ? void 0 : val.toLowerCase()) !== null && _a !== void 0 ? _a : '') > -1;
    }
    isNumeric(val) {
        return !isNaN(parseInt(val !== null && val !== void 0 ? val : ''));
    }
    getPixelToRem(pixels) {
        return `calc((${pixels} / 16) * 1em)`;
    }
    getCssValue(val) {
        var _a;
        const result = {
            value: undefined,
            unit: undefined
        };
        if (!val) {
            return result;
        }
        const matches = val.match(SiriusIconComponent.CSS_VALUE_REGEX);
        const value = parseInt((_a = matches === null || matches === void 0 ? void 0 : matches.at(0)) !== null && _a !== void 0 ? _a : '');
        result.value = !isNaN(value) ? value : undefined;
        result.unit = matches === null || matches === void 0 ? void 0 : matches.at(1);
        return result;
    }
    onIconReady(event) {
        var _a;
        const value = this.getArgument(' ', 0, this._requestedResource);
        const resource = (_a = event.detail) !== null && _a !== void 0 ? _a : this.getArguments(':', value).slice(-1).pop();
        if (!resource) {
            return;
        }
        if (this.iconReadyCallback) {
            this.iconRegistry.unregisterIconReadyCallback(resource, this.iconReadyCallback);
        }
        this.requestUpdate('asset', resource);
    }
    getArguments(separator, input) {
        var _a;
        return (_a = input === null || input === void 0 ? void 0 : input.split(separator).map(val => val.trim()).filter(val => val)) !== null && _a !== void 0 ? _a : [];
    }
    getArgument(separator, extractIndex, input) {
        const args = this.getArguments(separator, input);
        return args.find((val, index) => index === extractIndex);
    }
}
SiriusIconComponent.CUSTOM_ELEMENT_NAME = 'sirius-icon';
SiriusIconComponent.styles = css `
        @keyframes fadeInAndOut{0%,to{opacity:0}75%{opacity:1}}
        
        :host([shape$=" up"]), :host([direction=up]), :host([dir=up]) {
            transform: rotate(0deg);
        }

        :host([shape$=" down"]), :host([direction=down]), :host([dir=down]) {
            transform: rotate(180deg);
        }

        :host([shape$=" left"]), :host([direction=left]), :host([dir=left]) {
            transform: rotate(270deg);
        }

        :host([shape$=" right"]), :host([direction=right]), :host([dir=right]) {
            transform: rotate(90deg);
        }

        :host([flip=horizontal]) {
            transform: scale(-1) rotateX(180deg);
        }

        :host([flip=vertical]) {
            transform: scale(-1) rotateY(180deg);
        }

        :host([inverse]) {
            -webkit-filter: invert(100%);
            filter: invert(100%);
        }

        :host([status=info]) {
            fill: #0077b8;
        }

        :host([status=success]) {
            fill: #2e8500;
        }

        :host([status=warning]) {
            fill: #c27b00;
        }

        :host([status=danger]) {
            fill: #e02200;
        }

        .icon-container {
            width: 100%;
            height: 100%;
            display: inline-flex;
            flex-wrap: nowrap;
            align-content: center;
            justify-content: center;
            align-items: center;
            pointer-events: none;
            contain: strict;
            font-size: 16px;
        }

        .icon-container > img {
            width: 100%;
            height: 100%;
            object-fit: contain;
            pointer-events: none;
        }
        
        .icon-container > .unknown-icon {
            width: 80%;
            height: 80%;
        }

        .icon-container > svg {
            width: 100%;
            height: 100%;
            transition: inherit;
            pointer-events: none;
            fill: currentColor;
        }
        
        .icon-container > svg .sirius-dot-3  {
            animation: 1.8s ease-out 0.675s infinite normal none running fadeInAndOut;
            stroke: currentColor;
        }
        
        .icon-container > svg .sirius-dot-2  {
            animation: 1.8s ease-out 0.422s infinite normal none running fadeInAndOut;
            stroke: currentColor;
        }
        
        .icon-container > svg .sirius-dot-1  {
            animation: 1.8s ease-in 0s infinite normal none running fadeInAndOut;
            stroke: currentColor;
        }
        
        .icon-container > svg title {
            display: none;
        }
    `;
SiriusIconComponent.T_SHIRT_SIZES = {
    xs: '0.4em',
    sm: '0.8em',
    md: '1.2em',
    lg: '1.8em',
    xl: '2.4em',
    xxl: '2.8em'
};
SiriusIconComponent.SUPPORTED_DIRECTIONS = [
    "up",
    "down",
    "left",
    "right"
];
SiriusIconComponent.CSS_VALUE_REGEX = /(\d+\s?)(cm|mm|in|px|pt|pc|em|ex|ch|rem|vw|vh|vmin|vmax|%|)(\s+|$)/gi;
SiriusIconComponent.SVG_MARKER = '<svg';
SiriusIconComponent.UNKNOWN_RESOURCE_SVG = '<svg class="unknown-icon" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle class="sirius-dot-3" cx="31.1" cy="18" r="2.9"/><circle class="sirius-dot-2" cx="18" cy="18" r="2.9"/><circle class="sirius-dot-1" cx="4.9" cy="18" r="2.9"/></svg>';
SiriusIconComponent.DEFAULT_ICON_SIZE = 'md';
__decorate([
    property({ type: String })
], SiriusIconComponent.prototype, "alt", void 0);
__decorate([
    state()
], SiriusIconComponent.prototype, "_cssSize", void 0);
__decorate([
    property({ type: String })
], SiriusIconComponent.prototype, "size", null);
__decorate([
    property({ type: Boolean })
], SiriusIconComponent.prototype, "solid", null);
__decorate([
    property({ type: Boolean })
], SiriusIconComponent.prototype, "failover", null);
__decorate([
    property({ type: String })
], SiriusIconComponent.prototype, "resource", null);
__decorate([
    property({ type: String })
], SiriusIconComponent.prototype, "shape", null);
__decorate([
    property({ type: String })
], SiriusIconComponent.prototype, "set", null);
__decorate([
    state()
], SiriusIconComponent.prototype, "resultedResource", null);
if (!customElements.get(SiriusIconComponent.CUSTOM_ELEMENT_NAME)) {
    customElements.define(SiriusIconComponent.CUSTOM_ELEMENT_NAME, SiriusIconComponent);
}
//# sourceMappingURL=data:application/json;base64,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